import {
  Children,
  Fragment,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { ReactElement } from 'react';
import checkedIcon from '@library/assets/images/icons/checked-icon.svg';
import { cn } from '@library/component-utils/cn';

type StepsState = {
  name: string;
  isComplete: boolean;
  isDirty: boolean;
};

type StepsContextValue = {
  currentStep: string;
  setCurrentStep: (value: string) => void;
  steps: StepsState[];
  setSteps: (steps: StepsState[]) => void;
  isStepComplete: () => boolean;
  setStepComplete: () => void;
  isStepFormDirty: () => boolean;
  setStepFormDirty: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StepsContext = createContext<StepsContextValue | undefined>(undefined);

export const useSteps = () => {
  const context = useContext(StepsContext) as StepsContextValue;

  if (context) {
    return context;
  }

  throw new Error('useSteps must be used within a StepProvider');
};

type StepsIndicatorProps = {
  children: string;
  step: string;
  number?: number;
};

export const StepsIndicator = ({
  children,
  step,
  number = 0
}: StepsIndicatorProps) => {
  const { currentStep, steps } = useSteps();
  const stepData = steps.find((s) => s.name === step);
  const isActive = step === currentStep;
  const isComplete = stepData?.isComplete;

  return (
    <div className="flex grow items-center px-4 py-2">
      {isActive && (
        <div className="border-atlien-200 text-atlien-200 flex size-10 items-center justify-center rounded-full border-2">
          <span className="font-semibold">{number}</span>
        </div>
      )}
      {!isActive && isComplete && (
        <div className="text-soClean-100 bg-atlien-200 flex size-10 items-center justify-center rounded-full">
          <img
            className="size-6 shrink-0"
            src={checkedIcon}
            alt="step complete"
          />
        </div>
      )}
      {!isActive && !isComplete && (
        <div className="border-soClean-160 text-soClean-160 flex size-10 items-center justify-center rounded-full border-2">
          <span className="font-semibold">{number}</span>
        </div>
      )}
      <span
        className={cn('text-soClean-160 ml-4 text-sm font-medium', {
          'text-soClean-100': isActive
        })}
      >
        {children}
      </span>
    </div>
  );
};

const Divider = () => {
  return (
    <div aria-hidden="true" className="right-0 top-0 h-full w-4">
      <svg
        fill="none"
        viewBox="0 0 22 80"
        preserveAspectRatio="none"
        className="text-soFresh-140 size-full"
      >
        <path
          d="M0 -2L20 40L0 82"
          stroke="currentColor"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

type StepsListProps = {
  children: ReactElement<StepsIndicatorProps>[];
};

export const StepsList = ({ children }: StepsListProps) => {
  const { steps, setSteps } = useSteps();

  useEffect(() => {
    const newSteps: StepsState[] = Children.map(children, (child) => {
      return {
        name: child.props.step,
        isComplete: false,
        isDirty: false
      };
    });

    setSteps(newSteps);
  }, [children, setSteps]);

  return (
    <div className="w-full">
      <div className="border-soFresh-140 flex items-center rounded-lg border">
        {Children.map(children, (child, index: number) => {
          return (
            <Fragment key={child.props.step}>
              {cloneElement(child, { number: index + 1 })}
              {index + 1 < steps.length && <Divider />}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

type StepsContentsProps = {
  children: ReactElement | ReactElement[];
  step: string;
};

export const StepsContents = ({ children, step }: StepsContentsProps) => {
  const { currentStep } = useSteps();
  return step === currentStep ? (
    <div className="flex flex-col gap-y-4" key={step}>
      {children}
    </div>
  ) : null;
};

type StepsProps = {
  children: ReactElement[];
  defaultStep: string;
};

export const Steps = ({ children, defaultStep }: StepsProps) => {
  const [steps, setSteps] = useState<StepsState[]>([]);
  const [currentStep, setCurrentStep] = useState(defaultStep);

  const isStepComplete = useCallback(
    () => !!steps.find((step) => step.name === currentStep)?.isComplete,
    [steps, currentStep]
  );

  const setStepComplete = useCallback(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step) =>
        step.name === currentStep ? { ...step, isComplete: true } : step
      )
    );
  }, [currentStep]);

  const isStepFormDirty = useCallback(
    () => !!steps.find((step) => step.name === currentStep)?.isDirty,
    [steps, currentStep]
  );

  const setStepFormDirty = useCallback(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step) =>
        step.name === currentStep ? { ...step, isDirty: true } : step
      )
    );
  }, [currentStep]);

  const value = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      steps,
      setSteps,
      isStepComplete,
      setStepComplete,
      isStepFormDirty,
      setStepFormDirty
    }),
    [
      currentStep,
      isStepComplete,
      isStepFormDirty,
      setStepComplete,
      setStepFormDirty,
      steps
    ]
  );

  return (
    <StepsContext.Provider value={value}>{children}</StepsContext.Provider>
  );
};
